import React, { useEffect, useMemo, useState } from "react";
import AdminAppBar from "./adminAppbar";
import homeIcon from "../assets/home.png";
import paymentIcon from "../assets/payment.png";
import userIcon from "../assets/user.png";
import claimIcon from "../assets/claim.png";
import updateIcon from "../assets/update.png";
import reportIcon from "../assets/report.png";
import notifIcon from "../assets/notif.png";
import logoutIcon from "../assets/logout.png";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import useViewportWidth from "../hooks/useViewportWidth";
import { useAuth } from "../contexts/AuthContext";
import { businessApi } from "../services/api";
import { isNoBusinessError } from "../services/onboarding";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, tokens, isBusinessAdmin, isSuperAdmin, logout } = useAuth();
  const accessToken = tokens?.access_token;

  // Below 900px the 260px sidebar left ~55px of a 390px screen for the content pane, so it
  // becomes an off-canvas drawer instead. 900 is the boundary components/dashboard.js was
  // already tuned to. AC-MOBILE-F3.
  const isNarrow = useViewportWidth() < 900;
  const [navOpen, setNavOpen] = useState(false);

  // Close on pathname rather than inside handleNavClick: this also catches browser
  // back/forward, which the click handler never sees. AC-MOBILE-E3.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // The drawer is `position: fixed`, so the document behind it is still the scroller: a swipe
  // on the page beside the menu slid the content under it, and closing the drawer left the
  // partner further down a page they never navigated to. Locked on the document, only while
  // the drawer is open and only below 900px — the drawer's own `overflowY: auto` still
  // scrolls, so a menu taller than the viewport stays reachable. AC-MOBILE-F23.
  useEffect(() => {
    if (!(isNarrow && navOpen)) {
      return undefined;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isNarrow, navOpen]);

  // null = not known yet. A partner who never finished onboarding has no business, and
  // until now the only way back to /create-profile was an automatic redirect — so on any
  // other dashboard page they were stuck with no route forward (#29).
  const [hasBusiness, setHasBusiness] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !isBusinessAdmin) {
      // Back to unknown, not stale. If this component is reused across an auth change
      // (e.g. business_owner -> super_admin without a remount), a leftover `false` would
      // show a "Complete setup" link to someone who should never see it.
      setHasBusiness(null);
      return undefined;
    }
    businessApi
      .getMyProfile(accessToken)
      .then(() => {
        if (!cancelled) setHasBusiness(true);
      })
      .catch((error) => {
        // Only a genuine "no business" answer means unfinished onboarding. A network
        // blip must not offer a partner a setup link they don't need.
        if (!cancelled) setHasBusiness(!isNoBusinessError(error));
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, isBusinessAdmin]);

  // Define navigation items based on user role
  const navItems = useMemo(() => {
    if (isSuperAdmin) {
      return [
        { label: "Dashboard", icon: homeIcon, path: "/admin-dashboard", isClickable: true },
        { label: "Profile", icon: userIcon, path: "/admin-dashboard/profile", isClickable: true },
        { label: "Consumers", icon: userIcon, path: "/admin-dashboard/consumers", isClickable: false },
        { label: "Daily View", icon: reportIcon, path: "/admin-dashboard/daily-view", isClickable: false },
        { label: "Reports", icon: reportIcon, path: "/admin-dashboard/reports", isClickable: false },
        { label: "Notifications", icon: notifIcon, path: "/admin-dashboard/notifications", isClickable: false },
        { label: "Logout", icon: logoutIcon, path: null, isLogout: true, isClickable: true },
      ];
    } else if (isBusinessAdmin) {
      return [
        ...(hasBusiness === false
          ? [{ label: "Complete setup", icon: updateIcon, path: "/create-profile", isClickable: true }]
          : []),
        { label: "Dashboard", icon: homeIcon, path: "/admin-dashboard", isClickable: true },
        { label: "Payment Info", icon: paymentIcon, path: "/admin-dashboard/payment-info", isClickable: true },
        { label: "Profile", icon: userIcon, path: "/admin-dashboard/profile", isClickable: true },
        { label: "Redeem Beer", icon: claimIcon, path: "/admin-dashboard/redeem-beer", isClickable: true },
        { label: "Update Payment", icon: updateIcon, path: "/admin-dashboard/update-payment", isClickable: true },
        { label: "Reports", icon: reportIcon, path: "/admin-dashboard/reports", isClickable: false },
        { label: "Notifications", icon: notifIcon, path: "/admin-dashboard/notifications", isClickable: false },
        { label: "Logout", icon: logoutIcon, path: null, isLogout: true, isClickable: true },
      ];
    }
    return [];
  }, [isSuperAdmin, isBusinessAdmin, hasBusiness]);

  const navItemsExceptLogout = navItems.filter(item => !item.isLogout);
  const logoutItem = navItems.find(item => item.isLogout);

  useEffect(() => {
    if (!loading && !(isBusinessAdmin || isSuperAdmin)) {
      navigate("/admin-login", { replace: true });
    }
  }, [loading, isBusinessAdmin, isSuperAdmin, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/admin-login", { replace: true });
  };

  const handleNavClick = (item) => {
    // Only allow clicks on clickable items
    if (!item.isClickable) {
      return;
    }
    
    if (item.isLogout) {
      handleLogout();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const isActive = (path) => {
    if (path === "/admin-dashboard") {
      return location.pathname === "/admin-dashboard";
    }
    return location.pathname === path;
  };

  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Poppins, sans-serif",
          color: "#303030",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!(isBusinessAdmin || isSuperAdmin)) {
    return null;
  }

  return (
    <div
      style={{
        // 12px of side padding on a phone rather than 20. Between this and the content pane
        // below, the shell was spending 80px of a 390px screen on padding and leaving 310 for
        // the page — less than the 328px Stripe's embedded Connect component will render at,
        // so /admin-dashboard/payment-info could be dragged sideways. The vertical padding is
        // untouched: the drawer's top offset is measured from it. AC-MOBILE-F21.
        padding: isNarrow ? "20px 12px" : "20px",
        background: "#ffffff",
        minHeight: "100vh",
      }}
    >
      {/* Admin App Bar */}
      <div
        style={{
          marginBottom: "20px",
          borderRadius: "16px",
          overflow: "hidden",
          // Above the scrim (999) and the drawer (1000) while narrow, so the hamburger stays
          // tappable and the same button that opened the drawer can close it. The drawer
          // clears the bar with paddingTop below rather than sliding under it unseen.
          ...(isNarrow && { position: "relative", zIndex: 1001 }),
        }}
      >
        <AdminAppBar
          onMenuClick={isNarrow ? () => setNavOpen((open) => !open) : undefined}
          isMenuOpen={navOpen}
        />
      </div>

      {/* Scrim. Only while the drawer is open, and only narrow — it must never sit over the
          desktop layout. */}
      {isNarrow && navOpen && (
        <div
          onClick={() => setNavOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            zIndex: 999,
          }}
        />
      )}

      <div style={{ display: "flex", gap: "20px" }}>
        {/* Sidebar */}
        <div
          id="admin-nav-drawer"
          data-testid="admin-nav-drawer"
          style={{
            width: "260px",
            minHeight: "793px",
            background: "#303030",
            color: "white",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            padding: "20px",
            // On a desktop Logout is anchored to the bottom of a sidebar that is as tall as the
            // page — the usual sidebar shape, and left alone. On a phone the same rule anchors
            // it to the bottom of the VIEWPORT, because the drawer is top: 88px; bottom: 0 —
            // and a phone viewport is not fixed: iOS Safari's address bar collapses as you
            // scroll, the drawer grows by around 100px, and Logout slides down with it while
            // the menu above it stays put. Below 900px it is the last item of the menu instead.
            // AC-MOBILE-F22.
            justifyContent: isNarrow ? "flex-start" : "space-between",
            // Narrow only. position: fixed also takes the sidebar out of the flex row, so
            // the content pane gets the full width with no second change, and overlaying
            // beats pushing: pushing means animating marginLeft on a flex: 1 column, which
            // fights the flex algorithm and reflows every frame. AC-MOBILE-E4/E5.
            ...(isNarrow && {
              position: "fixed",
              // Below the app bar (which occupies y=20..80 inside the shell's 20px padding)
              // rather than behind it, so the bar stays a card instead of floating on top of
              // a full-height dark panel.
              top: "88px",
              left: 0,
              bottom: 0,
              width: "min(272px, 82vw)",
              minHeight: 0,
              // Square off the edge that meets the screen; keep the app's rounded card look
              // on the edge that meets the content.
              borderRadius: "0 12px 12px 0",
              overflowY: "auto",
              zIndex: 1000,
              transform: navOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.25s ease",
              // Only while it is over the content. translateX(-100%) puts the panel's right
              // edge exactly on x=0, but a shadow paints outside the box, so a 32px blur left
              // a dark strip down the left edge of every admin screen with the drawer shut.
              // AC-MOBILE-F20.
              boxShadow: navOpen ? "0 12px 32px rgba(0,0,0,0.35)" : "none",
            }),
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            {navItemsExceptLogout.map((item, index) => {
              const active = isActive(item.path);
              const isClickable = item.isClickable !== false; // Default to true if not specified
              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px",
                    cursor: isClickable ? "pointer" : "not-allowed",
                    borderRadius: "6px",
                    transition: "background 0.2s, opacity 0.2s",
                    background: active ? "#3a3a3a" : "transparent",
                  }}
                  onClick={() => handleNavClick(item)}
                  onMouseEnter={(e) => {
                    if (isClickable && !active) {
                      e.currentTarget.style.background = "#3a3a3a";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isClickable && !active) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "#6E6E6E",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                      borderRadius: "4px",
                      // opacity: isClickable ? 1 : 0.6,
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={item.label}
                      style={{ width: "24px", height: "24px" }}
                    />
                  </div>
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {item.label}
                  </span>
                  {item.isClickable === false && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "white",
                        marginLeft: "auto",
                        background: "#DE810E",
                        padding: "2px 4px",
                        borderRadius: "4px",
                      }}
                    >
                      Coming Soon
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Logout */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px",
              cursor: "pointer",
              borderRadius: "6px",
              transition: "background 0.2s",
              // A rule and a little air where it now follows the menu directly, so it still
              // reads as separate from the navigation rather than as a seventh destination.
              // AC-MOBILE-F22.
              ...(isNarrow && {
                marginTop: "14px",
                paddingTop: "18px",
                borderTop: "1px solid #4A4A4A",
                borderRadius: 0,
              }),
            }}
            onClick={handleLogout}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#3a3a3a")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "#6E6E6E",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "10px",
                borderRadius: "4px",
              }}
            >
              <img
                src={logoutItem.icon}
                alt={logoutItem.label}
                style={{ width: "24px", height: "24px" }}
              />
            </div>
            <span
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              {logoutItem.label}
            </span>
          </div>
        </div>

        {/* Main Content */}
        <div
          data-testid="admin-content"
          style={{
            flex: 1,
            borderRadius: "16px",
            // The other half of the 80px. 390 - 24 - 24 = 342px of usable width, which clears
            // the embed's 328 with room to spare. AC-MOBILE-F21.
            padding: isNarrow ? "16px 12px" : "20px",
            display: "flex",
            flexDirection: "column",
            // minWidth: 0 lets this flex child shrink below its content's intrinsic width
            // instead of forcing the row wider; overflow was "hidden", which clipped what
            // did not fit rather than letting it scroll. AC-MOBILE-F3.
            minWidth: 0,
            background: "#ffffff",
            boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1, overflowY: "auto", width: "100%" }}>
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
